import z from 'zod';
import { getAddressSchema } from '../../address';

// Base schema without refinements (so we can extend it)
const BaseExamSecuritySchema = z.object({
  heldAtVisitingAddress: z.boolean(),
  alternativeTestingAddress: z.any().optional(), // Address validation handled separately
  alternativeAddressExplanation: z.string().optional(),
  materialReceivedBy: z.string().min(1, 'Person responsible for receiving materials is required'),
  materialReceivingArea: z.array(z.string()).min(1, 'At least one receiving area must be selected'),
  materialReceivingAreaOther: z.string().optional(),
  materialStorageLocation: z.string().min(1, 'Material storage location is required'),
  isMaterialStorageLocked: z.boolean(),
  securityResponsible: z.string().min(1, 'Security responsible person is required'),
  allowNonEmployedAccess: z.boolean(),
  hasDesignatedTestingRooms: z.boolean(),
  hasTestingRoomPowerWifi: z.boolean().optional()
});

// Function to add refinements to any ExamSecurity schema
const addExamSecurityRefinements = (schema: any) => {
  return schema.refine(
    (data: any) => {
     // If testing is NOT held at visiting address, alternative testing address is required
      if (data.heldAtVisitingAddress === false) {
        return !!data.alternativeTestingAddress;
      }
      return true;
      },
      {
        message: 'Alternative testing address is required when testing is not held at visiting address',
        path: ['alternativeTestingAddress']
      }
    ).refine(
    (data: any) => {
      // If testing is NOT held at visiting address, alternative address explanation is required
      if (data.heldAtVisitingAddress === false) {
        return data.alternativeAddressExplanation && data.alternativeAddressExplanation.trim().length > 0;
      }
      return true;
    },
    {
      message: 'Explanation for alternative testing location is required when testing is not held at visiting address',
      path: ['alternativeAddressExplanation']
    }
  ).refine(
    (data: any) => {
      // If "Other" is selected in receiving area, specification is required
      if (data.materialReceivingArea?.includes('Other')) {
        return data.materialReceivingAreaOther && data.materialReceivingAreaOther.trim().length > 0;
      }
      return true;
    },
    {
      message: 'Please specify the other receiving location',
      path: ['materialReceivingAreaOther']
    }
  ).refine(
    (data: any) => {
      // If testing rooms are available, power/wifi question must be answered
      if (data.hasDesignatedTestingRooms === true) {
        return data.hasTestingRoomPowerWifi !== undefined;
      }
      return true;
    },
    {
      message: 'Please indicate if testing spaces will have power and WIFI access',
      path: ['hasTestingRoomPowerWifi']
    }
  );
};

// Default schema with refinements (for standalone use)
export const ExamSecuritySchema = addExamSecurityRefinements(BaseExamSecuritySchema);

// Function to get ExamSecurity schema with address validation
export const getExamSecuritySchema = (alternativeTestingAddress?: any) => {
  const AlternativeTestingAddressSchema = getAddressSchema(alternativeTestingAddress?.countryIsoCd);

  // Extend the base schema first, then add refinements
  const extendedSchema = BaseExamSecuritySchema.extend({
    alternativeTestingAddress: AlternativeTestingAddressSchema.optional()
  });

  // Apply all the refinements to the extended schema
  return addExamSecurityRefinements(extendedSchema);
};